import _, { find, get, isEmpty, keyBy, orderBy } from "lodash";
import { useRef } from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createPageTemplate, getClosestIndices } from "./helper";
import { fetchPage, updateActiveForIcV2 } from "actions/pages";
import { createEditorSession } from "actions/editor";
import { LIVE_ELEMENT, PAGE } from "constants/widgetTypes";
import { useEffect } from "react";
import { MultipageWrapper } from "./MultiPageWrapper";
import { registerEditorSessionReducer } from "components/Editor/hooks/useEditorSession";
import { fetchPageWithSnapshot } from "actions/versions";
import { InteractiveContentProvider, useInteractiveContentBreakpoints } from "@paperflite-marketing/cleverstory-commons";
import { useMemo } from "react";
const imageCdn = process.env.REACT_APP_IMAGE_CDN

const PAGES_TO_RENDER = 3;

export const InteractiveContentRenderer = ({
  page,
  storyboard,
  versionId,
}) => {
  const {
    pages = [],
    activePage,
    fetchedPages = [],
    pagesNotFetched = [],
  } = useSelector((state) => get(state, "pages.icV2", {}));
  const pagesById = keyBy(pages, "id");
  const [pagesWithFrames, setPagesWithFrames] = useState([])
  const activePageIndex = pages.findIndex((item) => item.id === activePage);
  const editorSessionCreated = useRef(new Set([page.canonical]));
  const dispatch = useDispatch();
  const { tabletScreens, extraSmallScreens } = useInteractiveContentBreakpoints()

  const interactiveContentValue = useMemo(() => ({
    imageCdn,
    tabletScreens,
    extraSmallScreens,
  }), [imageCdn, tabletScreens, extraSmallScreens]);


const handleLazyLoadPages = () => {
  const page = pages[activePageIndex];
  if (!page) return;

  const pagesToFetch = getClosestIndices(pages, page.id, PAGES_TO_RENDER);

  for (const item of pagesToFetch) {
    if (item.loaded || item.loading) continue;

    const page = find(pages, { id: item.id });

    if (versionId && versionId !== "current") {
      dispatch(fetchPageWithSnapshot({
        storyboard: storyboard.id,
        page: page.id,
        snapshot: versionId,
      }))
    } else {
      dispatch(fetchPage(page.id, true));
    }
  }
};

  const handleChange = (type, data) => {
    const { index } = data;
    const page = pages[index];
    if (!page) return;
    switch (type) {
      case "index":
        dispatch(updateActiveForIcV2(page.id));
        break;
    }
  };

  useEffect(() => {
    if (!fetchedPages.length) return;

    const allWidgets = fetchedPages.flatMap((page) => {
      const widgets = page?.draft?.widgets || page?.content?.widgets || [];
      if (!editorSessionCreated.current.has(page.id)) {
        registerEditorSessionReducer(`editors_${page.id}`);
        dispatch(createEditorSession(`editors_${page.id}`, { widgets }));
        editorSessionCreated.current.add(page.id);
      }
      return widgets;
    });

    allWidgets.filter(widget => widget.type === LIVE_ELEMENT).forEach(widget => {
      const sessionId = `editors_live_element_${widget.uid}`;
      if (!editorSessionCreated.current.has(widget.uid)) {
        registerEditorSessionReducer(sessionId);
        dispatch(createEditorSession(sessionId, {
          widgets: widget.children || []
        }));
        editorSessionCreated.current.add(widget.uid);
      }
    });

    const pageSections = allWidgets.filter((widget) => widget.type === PAGE);
    const pagesWithFrames = _.values(_.groupBy(pageSections, "pageId"));

    // Generate templates for not-yet-fetched pages
    const templates = pagesNotFetched.map((page) => [
      createPageTemplate(page.id),
    ]);

    const mergedPages = [...pagesWithFrames, ...templates];
    const sortedPagesWithFrames = _.sortBy(mergedPages, (group) => {
      // get the pageId of the first item in the group
      const pageId = group[0].pageId;
      return pagesById[pageId].order;
    });
    
    setPagesWithFrames(sortedPagesWithFrames)
  }, [fetchedPages.length, pagesNotFetched.length]);

  useEffect(() => {
    handleLazyLoadPages();
  }, [activePageIndex]);
  
  if(isEmpty(pagesById)) return null

  return (
    <InteractiveContentProvider value={interactiveContentValue}>
      <MultipageWrapper
        storyboard={storyboard}
        onChange={handleChange}
        pagesWithFrames={pagesWithFrames}
        activePageIndex={activePageIndex}
        activePage={activePage}
        pagesById={pagesById}
        />
    </InteractiveContentProvider>
  );
};
